import React, { useState, useEffect } from 'react';
import { X, User, AlertCircle, UserPlus } from 'lucide-react';
import guardianService from '../services/guardianService';
import { useAuth } from '../hooks/useAuth';

const GuardianStudentSelectDialog = ({ 
    isOpen, 
    onClose, 
    onSelectStudent, 
    courseId,
    courseName,
    existingWaitlistStudents = [],
    existingBookedStudents = []
}) => {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showNewStudentForm, setShowNewStudentForm] = useState(false);
    
    // New student form state
    const [newStudent, setNewStudent] = useState({
        name: '',
        dateOfBirth: '',
        gender: '',
        school: '',
        yearLevel: ''
    });

    useEffect(() => {
        console.log('GuardianStudentSelectDialog isOpen changed:', isOpen);
        if (isOpen) {
            console.log('Dialog opened, loading students');
            loadStudents();
        }
    }, [isOpen]);

    const loadStudents = async () => {
        try {
            setLoading(true);
            const response = await guardianService.getStudents();
            if (response?.students) {
                // Filter out students already on waitlist or booked
                const filteredStudents = response.students.filter(student => {
                    const isOnWaitlist = existingWaitlistStudents.some(
                        w => w.studentEmail === student.email || w.email === student.email
                    );
                    const isBooked = existingBookedStudents.some(
                        b => b.studentEmail === student.email || b.email === student.email
                    );
                    return !isOnWaitlist && !isBooked;
                });
                setStudents(filteredStudents);
            }
        } catch (error) {
            console.error('Failed to load students:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectStudent = () => {
        if (selectedStudent) {
            onSelectStudent(selectedStudent);
            onClose();
        }
    };

    const handleAddNewStudent = () => {
        if (newStudent.name && newStudent.dateOfBirth && newStudent.gender) {
            // Generate managed email with pattern: studentfirst_studentlast_parentlast_XXX@managed.student
            const nameParts = newStudent.name.trim().split(/\s+/);
            const studentFirstName = nameParts[0]?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'student';
            const studentLastName = nameParts.slice(1).join('').toLowerCase().replace(/[^a-z0-9]/g, '') || '';
            
            // Get parent's last name from the user context
            const parentName = user?.attributes?.family_name || user?.attributes?.name || '';
            const parentLastName = parentName.split(/\s+/).pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'guardian';
            
            // Generate 3 random alphanumeric characters
            const randomChars = Math.random().toString(36).substring(2, 5).toUpperCase();
            
            // Build the email with parent name included
            const managedEmail = studentLastName 
                ? `${studentFirstName}_${studentLastName}_${parentLastName}_${randomChars}@managed.student`
                : `${studentFirstName}_${parentLastName}_${randomChars}@managed.student`;
            
            const studentData = {
                isNew: true,
                name: newStudent.name,
                email: managedEmail,
                ...newStudent
            };
            onSelectStudent(studentData);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h3 className="text-lg font-semibold">Select Student for Waitlist</h3>
                            <p className="text-sm text-gray-600 mt-1">{courseName}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-gray-600">Loading students...</p>
                        </div>
                    ) : (
                        <>
                            {!showNewStudentForm ? (
                                <>
                                    {/* Student List */}
                                    {students.length > 0 ? (
                                        <div className="space-y-2 mb-4">
                                            <p className="text-sm text-gray-600 mb-3">
                                                Select which student to add to the waitlist:
                                            </p>
                                            {students.map((student) => (
                                                <label
                                                    key={student.email}
                                                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                                        selectedStudent?.email === student.email
                                                            ? 'border-blue-500 bg-blue-50'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="student"
                                                        value={student.email}
                                                        checked={selectedStudent?.email === student.email}
                                                        onChange={() => setSelectedStudent(student)}
                                                        className="sr-only"
                                                    />
                                                    <User className="h-5 w-5 text-gray-400 mr-3" />
                                                    <div className="flex-1">
                                                        <p className="font-medium text-gray-900">
                                                            {student.name || `${student.firstName} ${student.lastName}`}
                                                        </p>
                                                        {student.school && (
                                                            <p className="text-sm text-gray-500">{student.school}</p>
                                                        )}
                                                        {student.yearLevel && (
                                                            <p className="text-xs text-gray-500">{student.yearLevel}</p>
                                                        )}
                                                    </div>
                                                    {student.gender && (
                                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                                            student.gender === 'Male' 
                                                                ? 'bg-blue-100 text-blue-700'
                                                                : 'bg-pink-100 text-pink-700'
                                                        }`}>
                                                            {student.gender}
                                                        </span>
                                                    )}
                                                </label>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-6">
                                            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                                            <p className="text-gray-600">No eligible students found</p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                All your students are either already booked or on the waitlist for this course.
                                            </p>
                                        </div>
                                    )}

                                    {/* Add New Student Option */}
                                    <button
                                        onClick={() => setShowNewStudentForm(true)}
                                        className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
                                    >
                                        <UserPlus className="h-5 w-5" />
                                        Add New Student
                                    </button>
                                </>
                            ) : (
                                <>
                                    {/* New Student Form */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Student Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={newStudent.name}
                                                onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                                placeholder="Enter student's full name"
                                                required
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Date of Birth *
                                                </label>
                                                <input
                                                    type="date"
                                                    value={newStudent.dateOfBirth}
                                                    onChange={(e) => setNewStudent({...newStudent, dateOfBirth: e.target.value})}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Gender *
                                                </label>
                                                <select
                                                    value={newStudent.gender}
                                                    onChange={(e) => setNewStudent({...newStudent, gender: e.target.value})}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                                    required
                                                >
                                                    <option value="">Select</option>
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    School *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={newStudent.school}
                                                    onChange={(e) => setNewStudent({...newStudent, school: e.target.value})}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                                    placeholder="School name"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Year Level *
                                                </label>
                                                <select
                                                    value={newStudent.yearLevel}
                                                    onChange={(e) => setNewStudent({...newStudent, yearLevel: e.target.value})}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                                    required
                                                >
                                                    <option value="">Select</option>
                                                    <option value="Year 7">Year 7</option>
                                                    <option value="Year 8">Year 8</option>
                                                    <option value="Year 9">Year 9</option>
                                                    <option value="Year 10">Year 10</option>
                                                    <option value="Year 11">Year 11</option>
                                                    <option value="Year 12">Year 12</option>
                                                </select>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => setShowNewStudentForm(false)}
                                            className="text-sm text-gray-600 hover:text-gray-800"
                                        >
                                            ← Back to student list
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                {!showNewStudentForm ? (
                                    <button
                                        onClick={handleSelectStudent}
                                        disabled={!selectedStudent}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Add to Waitlist
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleAddNewStudent}
                                        disabled={!newStudent.name || !newStudent.dateOfBirth || !newStudent.gender}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Add New Student
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GuardianStudentSelectDialog;